import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    FormControl,
    Button,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    TableHead
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, AttachFile as AttachFileIcon, Functions as FunctionsIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';

const FormResponsePreview = ({ formDataProp }) => {
    const params = useParams();
    const id = params?.id;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(!formDataProp);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState(formDataProp || null);
    
    const appToken = useSelector((state) => state.appToken.apptoken);
    const userData = useSelector((state) => state.loginData.loginData);

    // Cleanup localStorage on component unmount
    useEffect(() => {
        return () => {
            // Component unmount olduğunda localStorage'ı temizle
            localStorage.removeItem('currentResponseId');
            localStorage.removeItem('currentResponseData');
            localStorage.removeItem('currentFormContent');
            localStorage.removeItem('currentFormData');
            localStorage.removeItem('currentFormTitle');
            console.log('FormResponsePreview localStorage temizlendi');
        };
    }, []);

    useEffect(() => {
        // Önce localStorage'dan veri almayı dene
        const loadFormDataFromLocalStorage = () => {
            try {
                const currentResponseId = localStorage.getItem('currentResponseId');
                const currentResponseData = localStorage.getItem('currentResponseData');
                const currentFormContent = localStorage.getItem('currentFormContent');
                const currentFormData = localStorage.getItem('currentFormData');
                const currentFormTitle = localStorage.getItem('currentFormTitle');
                
                console.log('localStorage\'dan veri alınıyor...');
                console.log('currentResponseId:', currentResponseId);
                console.log('currentResponseData:', currentResponseData);
                console.log('currentFormContent:', currentFormContent);
                console.log('currentFormData:', currentFormData);
                
                if (currentResponseId && currentFormContent && currentFormData) {
                    // formContent'ı parse et
                    let formContentObj = {};
                    if (typeof currentFormContent === 'string') {
                        try {
                            // Önce dıştaki tırnak işaretlerini kaldır
                            let cleanContent = currentFormContent.replace(/^"(.*)"$/, '$1');
                            // Escape edilmiş karakterleri düzelt
                            cleanContent = cleanContent.replace(/\\"/g, '"');
                            // JSON parse et
                            formContentObj = JSON.parse(cleanContent);
                        } catch (parseError) {
                            console.error('formContent parse hatası:', parseError);
                            // Direkt parse etmeyi dene
                            formContentObj = JSON.parse(currentFormContent);
                        }
                    } else if (typeof currentFormContent === 'object') {
                        formContentObj = currentFormContent;
                    }

                    // formData'yı parse et
                    let formDataObj = {};
                    if (typeof currentFormData === 'string') {
                        try {
                            // Önce dıştaki tırnak işaretlerini kaldır
                            let cleanData = currentFormData.replace(/^"(.*)"$/, '$1');
                            // Escape edilmiş karakterleri düzelt
                            cleanData = cleanData.replace(/\\"/g, '"');
                            // JSON parse et
                            formDataObj = JSON.parse(cleanData);
                        } catch (parseError) {
                            console.error('formData parse hatası:', parseError);
                            // Direkt parse etmeyi dene
                            formDataObj = JSON.parse(currentFormData);
                        }
                    } else if (typeof currentFormData === 'object') {
                        formDataObj = currentFormData;
                    }

                    console.log('Parse edilmiş formContentObj:', formContentObj);
                    console.log('Parse edilmiş formDataObj:', formDataObj);

                    // Form alanlarını kullanıcı değerleriyle birleştir
                    const fieldsWithValues = formContentObj.fields?.map(field => {
                        const fieldData = formDataObj.formData?.[field.id];
                        
                        // Alan tipine göre değer atama
                        let fieldValue = '';
                        if (field.type === 'file') {
                            // Öncelik: files > formData.value
                            let fileValue = formDataObj.files?.[field.id];
                            if (!fileValue || (Array.isArray(fileValue) && fileValue.length === 0)) {
                                if (fieldData?.value) {
                                    fileValue = fieldData.value;
                                }
                            }
                            fieldValue = fileValue || [];
                        } else if (field.type === 'signature') {
                            // İmza alanı için özel işlem
                            if (fieldData?.value) {
                                fieldValue = fieldData.value;
                            } else if (formDataObj.signature) {
                                fieldValue = formDataObj.signature;
                            } else if (formDataObj.formData?.signature) {
                                fieldValue = formDataObj.formData.signature;
                            }
                        } else if (field.type === 'name') {
                            // Name alanı için özel işlem
                            fieldValue = fieldData?.value || '';
                            if (typeof fieldValue === 'object') {
                                fieldValue = fieldValue.value || `${fieldValue.firstName || ''} ${fieldValue.lastName || ''}`.trim();
                            }
                        } else {
                            fieldValue = fieldData?.value || '';
                        }
                        
                        return {
                            ...field,
                            value: fieldValue,
                            label: fieldData?.label || field.label || '',
                            type: field.type || 'text'
                        };
                    }) || [];

                    setFormData({
                        title: currentFormTitle || formContentObj.title || 'Form Yanıtı',
                        description: formContentObj.description || '',
                        fields: fieldsWithValues,
                        settings: formContentObj.settings || {},
                        signature: formDataObj.signature || formDataObj.formData?.signature || null,
                        files: formDataObj.files || {}
                    });
                    
                    setLoading(false);
                    return true; // localStorage'dan veri alındı
                }
                return false; // localStorage'dan veri alınamadı
            } catch (error) {
                console.error('localStorage\'dan veri alınırken hata:', error);
                return false;
            }
        };

        // URL'den gelen id varsa ve formDataProp yoksa API çağrısı yap
        const loadFormDataFromAPI = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`https://app.hhoms.com/api/form/LibraryRecords/${id}`, {
                    headers: {
                        'ApplicationToken': appToken,
                        'User': userData.id
                    }
                });

                if (response.data && response.data.formLibraryRecords?.[0]) {
                    const record = response.data.formLibraryRecords[0];
                    
                    // formContent'ı parse et (çift escape edilmiş string için)
                    let formContentObj = {};
                    if (typeof record.formContent === 'string') {
                        // Önce dıştaki tırnak işaretlerini kaldır
                        let cleanContent = record.formContent.replace(/^"(.*)"$/, '$1');
                        // Escape edilmiş karakterleri düzelt
                        cleanContent = cleanContent.replace(/\\"/g, '"');
                        // JSON parse et
                        formContentObj = JSON.parse(cleanContent);
                    } else if (typeof record.formContent === 'object') {
                        formContentObj = record.formContent;
                    }

                    // formData'yı parse et (çift escape edilmiş string için)
                    let formDataObj = {};
                    if (typeof record.formData === 'string') {
                        // Önce dıştaki tırnak işaretlerini kaldır
                        let cleanData = record.formData.replace(/^"(.*)"$/, '$1');
                        // Escape edilmiş karakterleri düzelt
                        cleanData = cleanData.replace(/\\"/g, '"');
                        // JSON parse et
                        formDataObj = JSON.parse(cleanData);
                    } else if (typeof record.formData === 'object') {
                        formDataObj = record.formData;
                    }

                    // Form alanlarını kullanıcı değerleriyle birleştir
                    const fieldsWithValues = formContentObj.fields.map(field => {
                        const fieldData = formDataObj.formData?.[field.id];
                        
                        // Alan tipine göre değer atama
                        let fieldValue = '';
                        if (field.type === 'file') {
                            // Öncelik: files > formData.value
                            let fileValue = formDataObj.files?.[field.id];
                            if (!fileValue || (Array.isArray(fileValue) && fileValue.length === 0)) {
                                if (fieldData?.value) {
                                    fileValue = fieldData.value;
                                }
                            }
                            fieldValue = fileValue || [];
                        } else if (field.type === 'signature') {
                            // İmza alanı için özel işlem
                            if (fieldData?.value) {
                                fieldValue = fieldData.value;
                            } else if (formDataObj.signature) {
                                fieldValue = formDataObj.signature;
                            } else if (formDataObj.formData?.signature) {
                                fieldValue = formDataObj.formData.signature;
                            }
                        } else if (field.type === 'name') {
                            // Name alanı için özel işlem
                            fieldValue = fieldData?.value || '';
                            if (typeof fieldValue === 'object') {
                                fieldValue = fieldValue.value || `${fieldValue.firstName || ''} ${fieldValue.lastName || ''}`.trim();
                            }
                        } else {
                            fieldValue = fieldData?.value || '';
                        }
                        
                        return {
                            ...field,
                            value: fieldValue,
                            label: fieldData?.label || field.label || '',
                            type: field.type || 'text'
                        };
                    });

                    setFormData({
                        title: formContentObj.title,
                        description: formContentObj.description,
                        fields: fieldsWithValues,
                        settings: formContentObj.settings,
                        signature: formDataObj.signature || formDataObj.formData?.signature || null,
                        files: formDataObj.files || {}
                    });
                }
            } catch (error) {
                console.error('Form verisi yüklenirken hata:', error);
                setError('Form verisi yüklenirken bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        };

        // Önce localStorage'dan veri almayı dene
        if (!formDataProp) {
            const localStorageSuccess = loadFormDataFromLocalStorage();
            if (!localStorageSuccess && id) {
                // localStorage'dan veri alınamadıysa API'den al
                loadFormDataFromAPI();
            }
        } else {
            // Props'tan gelen veri varsa direkt onu kullan
            setFormData(formDataProp);
            setLoading(false);
        }
    }, [id, appToken, userData.id, formDataProp]);

    const parseCellReference = (ref) => {
        try {
            const col = ref.match(/[A-Z]+/)[0];
            const row = parseInt(ref.match(/\d+/)[0]);
            return [col, row];
        } catch (error) {
            console.error('Hücre referansı ayrıştırma hatası:', error);
            return ['A', 1];
        }
    };

    const calculateFormula = (formula, rowId) => {
        try {
            if (formula.startsWith('TOPLA(')) {
                const range = formula.match(/TOPLA\((.*?)\)/)[1];
                const cells = range.split(',');
                let sum = 0;
                cells.forEach(cellRef => {
                    const [col, row] = parseCellReference(cellRef);
                    const value = getCellValue(row, col);
                    let cellResult = value;
                    if (typeof value === 'string' && value.startsWith('=')) {
                        cellResult = calculateFormula(value.substring(1), row);
                    }
                    sum += parseNumber(cellResult);
                });
                return formatNumber(sum);
            }

            if (formula.startsWith('ORTALAMA(')) {
                const range = formula.match(/ORTALAMA\((.*?)\)/)[1];
                const cells = range.split(',');
                let sum = 0;
                let count = 0;
                cells.forEach(cellRef => {
                    const [col, row] = parseCellReference(cellRef);
                    const value = getCellValue(row, col);
                    let cellResult = value;
                    if (typeof value === 'string' && value.startsWith('=')) {
                        cellResult = calculateFormula(value.substring(1), row);
                    }
                    const numValue = parseNumber(cellResult);
                    if (!isNaN(numValue)) {
                        sum += numValue;
                        count++;
                    }
                });
                return count > 0 ? formatNumber(sum / count) : '#HATA!';
            }

            formula = formula.replace(/([A-Z]+)(\d+)/g, (match, col, row) => {
                const value = getCellValue(parseInt(row), col);
                let cellResult = value;
                if (typeof value === 'string' && value.startsWith('=')) {
                    cellResult = calculateFormula(value.substring(1), parseInt(row));
                }
                return parseNumber(cellResult).toString();
            });

            formula = formula.replace(/,/, '.');

            if (!/^[0-9+\-*/().\s]+$/.test(formula)) return '#HATA!';

            const result = Function(`"use strict";return (${formula})`)();
            if (isNaN(result) || !isFinite(result)) return '#HATA!';
            return formatNumber(result);
        } catch (error) {
            console.error('Formül hesaplama hatası:', error);
            return '#HATA!';
        }
    };

    const getCellValue = (rowId, columnId) => {
        if (!formData) return 0;
        const field = formData.fields.find(f => f.type === 'calculation');
        if (!field || !field.value) return 0;
        
        const parsedValue = typeof field.value === 'string' ? JSON.parse(field.value) : field.value;
        const row = parsedValue.rows.find(r => r.id === rowId);
        if (!row) return 0;
        
        const value = row[columnId];
        if (value === undefined || value === null || value === '') return 0;
        return value;
    };

    const parseNumber = (value) => {
        if (typeof value === 'number') return value;
        if (!value || value === '' || value === '#HATA!') return 0;
        const str = value.toString().trim().replace(',', '.');
        if (str === '') return 0;
        const num = parseFloat(str);
        return isNaN(num) ? 0 : num;
    };

    const renderFieldValue = (field, value) => {
        if (!value) return null;

        switch (field.type) {
            case 'file':
                try {
                    // Eğer value bir string ise ve JSON formatındaysa parse et
                    let fileValue = value;
                    if (typeof value === 'string') {
                        try {
                            fileValue = JSON.parse(value);
                        } catch (e) {
                            // JSON parse edilemezse string olarak kullan
                            fileValue = value;
                        }
                    }
                    return <FileDisplay file={fileValue} />;
                } catch (error) {
                    console.error('Dosya görüntüleme hatası:', error);
                    return <Typography color="error">Dosya görüntülenemedi</Typography>;
                }

            case 'signature':
                try {
                    // Eğer value bir string ise ve JSON formatındaysa parse et
                    let signatureValue = value;
                    if (typeof value === 'string') {
                        try {
                            signatureValue = JSON.parse(value);
                        } catch (e) {
                            // JSON parse edilemezse string olarak kullan
                            signatureValue = value;
                        }
                    }
                    return <SignatureDisplay signature={signatureValue} />;
                } catch (error) {
                    console.error('İmza görüntüleme hatası:', error);
                    return <Typography color="error">İmza görüntülenemedi</Typography>;
                }

            case 'calculation':
                try {
                    // Hesaplama alanı için özel işlem
                    let calculationValue = value;
                    if (typeof value === 'string') {
                        try {
                            calculationValue = JSON.parse(value);
                        } catch (e) {
                            console.error('Hesaplama alanı parse hatası:', e);
                            return <Typography color="error">Hesaplama alanı verisi görüntülenemedi</Typography>;
                        }
                    }

                    // Veri yapısını kontrol et
                    if (!calculationValue.columns || !calculationValue.rows) {
                        return <Typography color="error">Hesaplama alanı verisi eksik</Typography>;
                    }

                    return (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper' }}>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    width: 50,
                                                    bgcolor: '#f5f5f5',
                                                    fontWeight: 'bold',
                                                    borderBottom: 2,
                                                    borderColor: 'divider',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                #
                                            </TableCell>
                                            {calculationValue.columns.map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    sx={{
                                                        backgroundColor: column.backgroundColor || '#ffffff',
                                                        fontWeight: 'bold',
                                                        borderBottom: 2,
                                                        borderColor: 'divider',
                                                        textAlign: column.textAlign || 'left',
                                                        whiteSpace: 'nowrap',
                                                        width: column.width || 'auto'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="subtitle2">
                                                            {column.label}
                                                        </Typography>
                                                        {column.isFormula && (
                                                            <Tooltip title="Formül Sütunu">
                                                                <FunctionsIcon fontSize="small" color="primary" />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {calculationValue.rows.map((row, rowIndex) => (
                                            <TableRow 
                                                key={rowIndex}
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: 'action.hover',
                                                    }
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        width: 50,
                                                        bgcolor: '#f5f5f5',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {rowIndex + 1}
                                                </TableCell>
                                                {calculationValue.columns.map((column) => (
                                                    <TableCell
                                                        key={column.id}
                                                        sx={{
                                                            backgroundColor: row.colors?.[column.id] || column.backgroundColor || '#ffffff',
                                                            textAlign: column.textAlign || 'left'
                                                        }}
                                                    >
                                                        {(() => {
                                                            const value = row[column.id];
                                                            if (typeof value === 'string' && value.startsWith('=')) {
                                                                try {
                                                                    const formula = value.substring(1);
                                                                    return calculateFormula(formula, row.id);
                                                                } catch (e) {
                                                                    return '#HATA!';
                                                                }
                                                            }
                                                            return value || '';
                                                        })()}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    );
                } catch (error) {
                    console.error('Hesaplama alanı görüntüleme hatası:', error);
                    return (
                        <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                            <Typography color="error">
                                Hesaplama alanı verisi görüntülenemedi
                            </Typography>
                            <Typography variant="caption" color="error">
                                Hata: {error.message}
                            </Typography>
                        </Box>
                    );
                }

            case 'text':
            case 'email':
            case 'phone':
                return (
                    <TextField
                        fullWidth
                        label={field.label}
                        value={value || ''}
                        disabled
                        margin="normal"
                    />
                );

            case 'textarea':
                return (
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={field.label}
                        value={value}
                        disabled
                        margin="normal"
                    />
                );

            case 'name':
                // Name alanı için özel görüntüleme
                const nameValue = typeof value === 'object' ? 
                    value.value || `${value.firstName || ''} ${value.lastName || ''}`.trim() : 
                    value || '';
        
                return (
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label={field.label}
                            value={nameValue}
                            disabled
                            margin="normal"
                            sx={{
                                '& .MuiInputBase-input': {
                                    bgcolor: '#f5f5f5'
                                }
                            }}
                        />
                    </Box>
                );

            case 'checkbox':
                return (
                    <FormGroup>
                        {field.options?.map((option, index) => (
                            <FormControlLabel
                                key={index}
                                control={
                                    <Checkbox
                                        checked={Array.isArray(value) ? value.includes(option) : false}
                                        disabled
                                    />
                                }
                                label={option}
                            />
                        ))}
                    </FormGroup>
                );

            case 'radio':
                return (
                    <FormControl component="fieldset">
                        <RadioGroup value={value}>
                            {field.options?.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={option}
                                    control={<Radio disabled />}
                                    label={option}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                );

            case 'table':
                return (
                    <Box sx={{ mt: 2, overflow: 'auto' }}>
                        <Paper variant="outlined">
                            <Table size="small">
                                <TableBody>
                                    {value?.rows?.map((row, rowIndex) => (
                                        <TableRow key={rowIndex}>
                                            {Object.entries(row).map(([key, cell], cellIndex) => (
                                                <TableCell 
                                                    key={`${rowIndex}-${cellIndex}`}
                                                    sx={{
                                                        border: '1px solid #e0e0e0',
                                                        padding: '12px',
                                                        backgroundColor: '#fff'
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {cell || ''}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>
                );

            default:
                // Eğer value bir dosya/görsel objesi ise FileDisplay ile göster
                if (
                    (typeof value === 'object' && value !== null && (value.base64 || value.name || value.type)) ||
                    (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && (value[0].base64 || value[0].name || value[0].type))
                ) {
                    return <FileDisplay file={value} />;
                }
                // Eğer value bir object ise, JSON.stringify ile göster
                let displayValue = value;
                if (typeof value === 'object') {
                    try {
                        displayValue = JSON.stringify(value, null, 2);
                    } catch (e) {
                        displayValue = '[object Object]';
                    }
                }
                return (
                    <TextField
                        fullWidth
                        label={field.label}
                        value={displayValue}
                        disabled
                        margin="normal"
                        multiline={typeof value === 'object'}
                        minRows={typeof value === 'object' ? 2 : 1}
                    />
                );
        }
    };

    const formatNumber = (value) => {
        if (value === undefined || value === null || value === '') return '';
        
        try {
            const strValue = value.toString().replace(/\./g, '').replace(',', '.');
            const num = parseFloat(strValue);
            
            if (isNaN(num)) return '';
            
            return new Intl.NumberFormat('tr-TR', {
                minimumFractionDigits: num % 1 === 0 ? 0 : 2,
                maximumFractionDigits: 2,
                useGrouping: true
            }).format(num);
        } catch (error) {
            console.error('Sayı formatlama hatası:', error);
            return value;
        }
    };

    // Dosya ve görsel önizleme için yardımcı fonksiyon
    const isImageFile = (fileName = '', mimeType = '') => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff'];
        const ext = fileName ? fileName.toLowerCase().slice(fileName.lastIndexOf('.')) : '';
        return mimeType.startsWith('image/') || imageExtensions.includes(ext);
    };

    // Dosya ve görsel önizleme için yardımcı fonksiyon
    const getFileIcon = (fileName = '', mimeType = '') => {
        const ext = fileName ? fileName.toLowerCase().slice(fileName.lastIndexOf('.')) : '';
        if (mimeType.startsWith('image/')) return { icon: null, color: '#f5f5f5' };
        if (ext === '.pdf') return { icon: <AttachFileIcon sx={{ color: '#d32f2f' }} />, color: '#ffebee' };
        if (ext === '.doc' || ext === '.docx') return { icon: <AttachFileIcon sx={{ color: '#1976d2' }} />, color: '#e3f2fd' };
        if (ext === '.xls' || ext === '.xlsx') return { icon: <AttachFileIcon sx={{ color: '#388e3c' }} />, color: '#e8f5e9' };
        if (ext === '.zip' || ext === '.rar') return { icon: <AttachFileIcon sx={{ color: '#fbc02d' }} />, color: '#fffde7' };
        return { icon: <AttachFileIcon color="primary" />, color: '#f5f5f5' };
    };

    // İmza görüntüleme için optimize edilmiş yardımcı bileşen
    const SignatureDisplay = ({ signature }) => {
        if (!signature) return null;

        try {
            // Eğer signature bir string ise ve JSON formatındaysa parse et
            let signatureValue = signature;
            if (typeof signature === 'string') {
                try {
                    signatureValue = JSON.parse(signature);
                } catch (e) {
                    // JSON parse edilemezse string olarak kullan
                    signatureValue = signature;
                }
            }

            // Base64 formatındaki imza verisi varsa göster
            if (typeof signatureValue === 'string' && signatureValue.startsWith('data:image/')) {
                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        bgcolor: '#f9f9f9'
                    }}>
                        <Typography variant="caption" color="text.secondary">
                            İmza
                        </Typography>
                        <Box 
                            component="img" 
                            src={signatureValue} 
                            alt="İmza" 
                            sx={{ 
                                maxWidth: '300px',
                                maxHeight: '150px',
                                border: '1px solid #ccc',
                                borderRadius: 1,
                                bgcolor: 'white'
                            }}
                        />
                    </Box>
                );
            }

            // Object formatındaki imza verisi varsa
            if (typeof signatureValue === 'object' && signatureValue.dataURL) {
                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        bgcolor: '#f9f9f9'
                    }}>
                        <Typography variant="caption" color="text.secondary">
                            İmza
                        </Typography>
                        <Box 
                            component="img" 
                            src={signatureValue.dataURL} 
                            alt="İmza" 
                            sx={{ 
                                maxWidth: '300px',
                                maxHeight: '150px',
                                border: '1px solid #ccc',
                                borderRadius: 1,
                                bgcolor: 'white'
                            }}
                        />
                    </Box>
                );
            }

            return (
                <Typography variant="body2" color="text.secondary">
                    İmza mevcut ancak görüntülenemiyor
                </Typography>
            );
        } catch (error) {
            console.error('İmza görüntüleme hatası:', error);
            return (
                <Typography variant="body2" color="error">
                    İmza görüntülenirken hata oluştu
                </Typography>
            );
        }
    };

    // Dosya/görsel gösterimi
    const FileDisplay = ({ file }) => {
        if (!file) return null;

        const renderFileContent = (fileData) => {
            const fileName = typeof fileData === 'object' ? fileData.name : String(fileData);
            const fileUrl = typeof fileData === 'object' ? fileData.base64 : fileData;
            const mimeType = typeof fileData === 'object' ? fileData.type : '';
            const fileSize = typeof fileData === 'object' ? fileData.size : 0;
            const isImage = isImageFile(fileName, mimeType);
            const { icon, color } = getFileIcon(fileName, mimeType);

            // Dosya boyutunu formatla
            const formatFileSize = (bytes) => {
                if (!bytes || bytes === 0) return '';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
            };

            return (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    bgcolor: isImage ? '#f5f5f5' : color,
                    width: '100%',
                    justifyContent: 'space-between',
                    mb: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        bgcolor: '#eeeeee',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {isImage && fileUrl ? (
                            <Box sx={{ position: 'relative' }}>
                                <Box 
                                    component="img" 
                                    src={fileUrl} 
                                    alt={fileName} 
                                    sx={{ 
                                        width: 64, 
                                        height: 64, 
                                        objectFit: 'cover', 
                                        borderRadius: 1, 
                                        border: '1px solid #ccc',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'scale(1.05)'
                                        }
                                    }}
                                    onClick={() => {
                                        const newWindow = window.open();
                                        newWindow.document.write(`
                                            <html>
                                                <head>
                                                    <title>${fileName}</title>
                                                    <style>
                                                        body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; }
                                                        img { max-width: 90%; max-height: 90vh; object-fit: contain; }
                                                    </style>
                                                </head>
                                                <body>
                                                    <img src="${fileUrl}" alt="${fileName}" />
                                                </body>
                                            </html>
                                        `);
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ 
                                width: 64, 
                                height: 64, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                bgcolor: color,
                                borderRadius: 1,
                                border: '1px solid #90caf9'
                            }}>
                                {icon}
                            </Box>
                        )}
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                {fileName || 'Dosya'}
                            </Typography>
                            {fileSize > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(fileSize)}
                                </Typography>
                            )}
                            {!isImage && mimeType && (
                                <Typography variant="caption" color="text.secondary">
                                    {mimeType}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {fileUrl && (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                const link = document.createElement('a');
                                link.href = fileUrl;
                                link.download = fileName || 'download';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                            startIcon={<AttachFileIcon />}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                px: 2
                            }}
                        >
                            İndir
                        </Button>
                    )}
                </Box>
            );
        };

        if (Array.isArray(file)) {
            return (
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1,
                    maxHeight: '400px',
                    overflowY: 'auto',
                    p: 1
                }}>
                    {file.map((f, idx) => (
                        <Box key={idx}>{renderFileContent(f)}</Box>
                    ))}
                </Box>
            );
        }

        return renderFileContent(file);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            minHeight: formDataProp ? 'auto' : '100vh',
            bgcolor: '#f5f5f5'
        }}>
            {!formDataProp && (
                <Box sx={{ 
                    p: 2, 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 1,
                    bgcolor: '#f5f5f5'
                }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => {
                            // Geri dönüşte de localStorage'ı temizle
                            localStorage.removeItem('currentResponseId');
                            localStorage.removeItem('currentResponseData');
                            localStorage.removeItem('currentFormContent');
                            localStorage.removeItem('currentFormData');
                            localStorage.removeItem('currentFormTitle');
                            
                            // Browser history kullanarak bir önceki sayfaya git
                            // Eğer history yok ise answers sayfasına git
                            if (window.history.length > 1) {
                                window.history.back();
                            } else {
                                navigate('/answers');
                            }
                        }}
                        variant="outlined"
                        sx={{ mb: 2 }}
                    >
                        Geri Dön
                    </Button>
                </Box>
            )}

            <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                p: 3
            }}>
                <Paper sx={{ 
                    width: '210mm',
                    p: 3,
                    pb: 8, // Alt kısımda doküman numarası için boşluk
                    bgcolor: 'white',
                    boxShadow: 3,
                    margin: '0 auto',
                    position: 'relative' // Doküman numarası için relative pozisyon
                }}>
                    {formData && (
                        <>
                            <Typography variant="h5" gutterBottom>
                                {formData.title}
                            </Typography>

                            {formData.description && (
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {formData.description}
                                </Typography>
                            )}

                            {formData.fields.map((field, index) => (
                                <Box key={index} sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {field.label}
                                        {field.required && <span style={{ color: 'red' }}> *</span>}
                                    </Typography>
                                    {field.helpText && (
                                        <Typography variant="caption" display="block" gutterBottom>
                                            {field.helpText}
                                        </Typography>
                                    )}
                                    {renderFieldValue(field, field.value)}
                                </Box>
                            ))}

                            {/* Doküman Kontrol Bilgileri - Sağ alt köşe */}
                            {formData.settings?.documentControlEnabled && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        right: 24,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2
                                    }}
                                >
                                    {formData.settings?.department && (
                                        <Typography
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: 'text.secondary',
                                                fontWeight: 500
                                            }}
                                        >
                                            {formData.settings.department}
                                        </Typography>
                                    )}
                                    {formData.settings?.documentPrefix && formData.settings?.documentCode && (
                                        <Typography
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: 'text.secondary',
                                                fontFamily: 'Roboto Mono, monospace',
                                                fontWeight: 500,
                                                letterSpacing: '0.5px',
                                                bgcolor: 'grey.100',
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'grey.300'
                                            }}
                                        >
                                            {`${formData.settings.documentPrefix}-${formData.settings.documentCode}`}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default FormResponsePreview; 